import { useIntl } from 'react-intl';

import { sharedMessages } from '@/components/secondFreeTrial/messages';
import { useFormattedCurrency } from '@/hooks/purchase';
import { useFormattedPriceWithTax } from '@/hooks/purchase/useFormattedPriceWithTax';
import { usePricesState, usePurchaseParamsState } from '@/hooks/store';
import { getPrice, OneTimePurchasePlanCadence, PlanCadence } from '@/store/purchaseParams/types';

import messages from './messages';
import { Divider, OrderRow, OrderRowBold, OrderRowLight, OrderRowSmall, Wrapper } from './styles';

interface PurchaseDetailsInnerProps {
	originalPrice: number;
	currentPrice: number;
	isFreeTrial: boolean;
	plan: PlanCadence | OneTimePurchasePlanCadence;
	refreshedDetails?: boolean;
	showMonthlyAsDueToday?: boolean;
	showCostPerYear?: boolean;
	isSecondFreeTrial?: boolean;
	amountDue?: number;
	isOrganicTraffic: boolean;
}

export const PurchaseDetailsInner = ({
	amountDue,
	originalPrice,
	currentPrice,
	isFreeTrial,
	plan,
	showMonthlyAsDueToday,
	showCostPerYear,
	isSecondFreeTrial,
	isOrganicTraffic,
}: PurchaseDetailsInnerProps) => {
	const { formatMessage } = useIntl();
	const formatCurrency = useFormattedCurrency();
	const currentPriceWithTax = useFormattedPriceWithTax(currentPrice);
	const originalPriceWithTax = useFormattedPriceWithTax(originalPrice);

	const totalDueToday = showMonthlyAsDueToday && !isFreeTrial ? currentPrice : 0;
	const totalDueTodayWithTax = useFormattedPriceWithTax(amountDue || 0);

	const isDiscount = originalPrice !== currentPrice;

	return (
		<Wrapper>
			{isFreeTrial || showMonthlyAsDueToday ? (
				<OrderRowBold>
					{formatMessage(messages.totalDueToday)}
					<div>{totalDueToday === 0 ? formatCurrency(totalDueToday) : totalDueTodayWithTax}</div>
				</OrderRowBold>
			) : null}
			{plan === 'monthly' ? (
				<>
					{isDiscount ? (
						<>
							<OrderRowLight>
								{formatMessage(messages.subtotal)}
								<div>{formatCurrency(originalPrice)}</div>
							</OrderRowLight>
							<OrderRowLight>
								{formatMessage(messages.discount)}
								<div>{formatCurrency(currentPrice - originalPrice)}</div>
							</OrderRowLight>
							<Divider />
						</>
					) : null}
					{!showMonthlyAsDueToday && (
						<OrderRow>
							{formatMessage(messages.costPerMonth)}
							<div>{formatCurrency(currentPrice)}</div>
						</OrderRow>
					)}
					{isFreeTrial ? (
						<OrderRowSmall>{formatMessage(messages.monthlyPriceAfterTrial)}</OrderRowSmall>
					) : null}
					{showMonthlyAsDueToday ? (
						<OrderRowSmall>{formatMessage(messages.monthlyPriceUntilCancelled)}</OrderRowSmall>
					) : null}
				</>
			) : ['yearly', 'family'].includes(plan) ? (
				<>
					{isDiscount && isOrganicTraffic ? (
						<>
							<OrderRowLight>
								{formatMessage(messages.subtotalPerMonth)}
								<div>{formatCurrency(originalPrice / 12)}</div>
							</OrderRowLight>
							<OrderRowLight>
								{formatMessage(messages.discount)}
								<div>{formatCurrency((currentPrice - originalPrice) / 12)}</div>
							</OrderRowLight>
							<Divider />
						</>
					) : null}
					{showCostPerYear || !isOrganicTraffic ? (
						<OrderRow>
							{formatMessage(messages.costPerYear)}
							<div>{formatCurrency(currentPrice)}</div>
						</OrderRow>
					) : (
						<OrderRow>
							{formatMessage(messages.costPerMonth)}
							<div>{formatCurrency(currentPrice / 12)}</div>
						</OrderRow>
					)}
					<OrderRowSmall $isSecondFreeTrial={isSecondFreeTrial}>
						{isDiscount
							? formatMessage(
									isFreeTrial ? messages.annualPriceWithDiscountAfterTrial : messages.annualPriceWithDiscount,
									{
										discountValue: currentPriceWithTax,
										regularValue: originalPriceWithTax,
									},
								)
							: formatMessage(
									isFreeTrial
										? isSecondFreeTrial
											? sharedMessages.sharedPaymentDetailsInfo
											: messages.annualPriceAfterTrial
										: messages.annualPrice,
									{
										value: currentPriceWithTax,
									},
								)}
					</OrderRowSmall>
				</>
			) : plan === 'lifetime' ? (
				<>
					{isDiscount ? (
						<>
							<OrderRowLight>
								{formatMessage(messages.subtotal)}
								<div>{formatCurrency(originalPrice)}</div>
							</OrderRowLight>
							<OrderRowLight>
								{formatMessage(messages.discount)}
								<div>{formatCurrency(currentPrice - originalPrice)}</div>
							</OrderRowLight>
							<Divider />
						</>
					) : null}
					<OrderRow>
						{formatMessage(messages.lifetimeCost)}
						<div>{currentPriceWithTax}</div>
					</OrderRow>
					{isFreeTrial ? (
						<OrderRowSmall>{formatMessage(messages.lifetimePriceAfterTrial)}</OrderRowSmall>
					) : null}
				</>
			) : null}
		</Wrapper>
	);
};

const PurchaseDetails = ({
	showMonthlyAsDueToday,
	showCostPerYear,
	isSecondFreeTrial,
	refreshedDetails = false,
	isOrganicTraffic = true,
}: {
	refreshedDetails?: boolean;
	showMonthlyAsDueToday?: boolean;
	showCostPerYear?: boolean;
	isSecondFreeTrial?: boolean;
	isOrganicTraffic?: boolean;
}) => {
	const prices = usePricesState();
	const { plan, purchaseType } = usePurchaseParamsState();
	const isFreeTrial = purchaseType?.type === 'freetrial' || !!isSecondFreeTrial;

	return (
		<PurchaseDetailsInner
			amountDue={isFreeTrial ? 0 : getPrice(plan, prices.current)}
			isSecondFreeTrial={isSecondFreeTrial}
			plan={plan}
			originalPrice={getPrice(plan, prices.original)}
			currentPrice={getPrice(plan, prices.current)}
			isFreeTrial={purchaseType?.type === 'freetrial' || !!isSecondFreeTrial}
			showMonthlyAsDueToday={showMonthlyAsDueToday}
			showCostPerYear={showCostPerYear}
			refreshedDetails={refreshedDetails}
			isOrganicTraffic={isOrganicTraffic}
		/>
	);
};

export default PurchaseDetails;
